import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { motion, useAnimate } from "motion/react";
import { Button, Icon, cn } from "../../ui/index.tsx";
import { LogoMark } from "../../ui/logo.tsx";
import { themeFor } from "../../three/themes.ts";
import { daysLeft, fmtDate, fmtDateTime, fmtExpiry } from "../../format.ts";
import { CompatBadges, DetectionBadge, GameArt, GameStatusBadge } from "../_shared.tsx";
import { Aurora, CountUp, Ring, Springy, Tilt, rise, stagger, useCalm } from "../member/kit.tsx";
import { SOURCE_ICON, parseSource, ringFraction } from "./model.ts";
import type { Entitlement, Game } from "../../../shared/types.ts";

function LicenseCard({ e, game, fresh, onExtend }: { e: Entitlement; game: Game | undefined; fresh: boolean; onExtend: () => void }) {
  const navigate = useNavigate();
  const calm = useCalm();
  const [flashRef, flash] = useAnimate<HTMLDivElement>();
  const prevExp = useRef(e.expires_at);

  const theme = themeFor(e.game_id, game?.category);
  const lifetime = e.expires_at === null;
  const expired = !e.active;
  const left = lifetime ? null : daysLeft(e.expires_at!);
  const urgent = !expired && left !== null && left <= 7;
  const ringColor = expired ? "var(--color-faint)" : urgent ? "var(--color-warn)" : theme.a;
  const src = parseSource(e.source);

  // Flash when a card arrives after first paint or its expiry moves (redeem, purchase).
  useEffect(() => {
    const changed = prevExp.current !== e.expires_at;
    prevExp.current = e.expires_at;
    if ((changed || fresh) && !calm && flashRef.current) void flash(flashRef.current, { opacity: [1, 0] }, { duration: 2, ease: "easeOut" });
  }, [e.expires_at]);

  return (
    <Tilt max={4} className="group pane flex min-h-[21rem] flex-col overflow-hidden">
      <div className="absolute inset-0">
        <GameArt
          game={{ name: e.game_name, image_url: game?.image_url ?? "", category: game?.category ?? "other" }}
          className={cn("size-full transition-[filter,opacity] duration-500", expired ? "opacity-40 grayscale" : "group-hover:scale-[1.03]")}
        />
      </div>
      <div className="absolute inset-0" style={{ background: `linear-gradient(to top, var(--color-bg) 6%, rgb(7 7 10 / 0.78) 46%, ${theme.a}${expired ? "10" : "30"} 100%)` }} />
      <div className="absolute inset-0" style={{ background: `radial-gradient(90% 70% at 100% 0%, ${theme.b}${expired ? "00" : "26"}, transparent 60%)` }} />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-bg/80 to-transparent" />
      <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{ background: `radial-gradient(340px circle at var(--mx, 50%) var(--my, 50%), ${theme.a}33, transparent 60%)` }} />
      <div ref={flashRef} className="pointer-events-none absolute inset-0 opacity-0" style={{ boxShadow: `inset 0 0 0 2px ${theme.a}, inset 0 0 60px ${theme.a}66` }} />

      {expired && (
        <div className="pointer-events-none absolute right-4 top-16 -rotate-[8deg] rounded-md border-2 border-bad/80 px-3 py-1 font-display text-lg font-semibold uppercase tracking-[0.25em] text-bad/90 shadow-[0_0_24px_rgb(255_77_77/0.25)]">
          Expired
        </div>
      )}

      <div className="relative flex flex-1 flex-col justify-between gap-6 p-5">
        <div className="flex flex-wrap gap-1.5">
          {game && <GameStatusBadge status={game.status} />}
          {game && <DetectionBadge status={game.detection_status} />}
          {game && <CompatBadges game={game} className="contents" />}
          {!game && <span className="kicker">{e.game_id}</span>}
        </div>

        <div className="space-y-4">
          <h3 className="line-clamp-2 font-display text-2xl font-semibold uppercase leading-tight tracking-wide">{e.game_name}</h3>

          <div className="flex items-center gap-4">
            <Ring fraction={expired ? 0 : ringFraction(e)} size={76} stroke={5} color={ringColor} glow={lifetime && !expired}>
              {lifetime ? (
                <span className="font-display text-3xl leading-none" style={{ color: theme.a, textShadow: `0 0 14px ${theme.a}` }}>∞</span>
              ) : expired ? (
                <Icon name="clock" className="size-5 text-faint" />
              ) : (
                <span className="text-center leading-none">
                  <CountUp value={Math.max(0, left ?? 0)} className="font-display text-xl font-semibold tabular-nums" />
                  <span className="mt-0.5 block font-mono text-[9px] uppercase tracking-widest text-muted">{left === 1 ? "day" : "days"}</span>
                </span>
              )}
            </Ring>
            <div className="min-w-0 flex-1">
              <div title={lifetime ? "Never expires" : `${expired ? "Expired" : "Expires"} ${fmtDateTime(e.expires_at)}`}
                className={cn("text-sm font-semibold", expired ? "text-bad" : urgent ? "text-warn" : "text-fg")}>
                {fmtExpiry(e.expires_at)}
              </div>
              {!lifetime && <div className="text-xs text-muted">{expired ? "Ended" : "Ends"} {fmtDate(e.expires_at)}</div>}
              <div className="mt-1.5 flex items-center gap-1.5 text-xs text-muted" title={`${e.source} · granted ${fmtDate(e.granted_at)}`}>
                <Icon name={SOURCE_ICON[src.kind]} className="size-3.5 shrink-0" />
                <span className="truncate">{src.label}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-line/70 pt-4">
            {(!game || game.purchasable) && <Springy><Button variant={expired ? "primary" : "secondary"} size="sm" icon="plus" onClick={onExtend}>{expired ? "Renew" : "Extend"}</Button></Springy>}
            <Button variant="ghost" size="sm" icon="download" onClick={() => navigate("/downloads")}>Downloads</Button>
          </div>
        </div>
      </div>
      {/* Corner brackets sit above the cover art, tinted per game. */}
      <div aria-hidden className="hud pointer-events-none" style={{ position: "absolute", inset: 0, ["--c" as string]: expired ? "var(--color-line-strong)" : theme.a }} />
    </Tilt>
  );
}

function EmptyLibrary({ onRedeem, onBrowse }: { onRedeem: () => void; onBrowse: () => void }) {
  const calm = useCalm();
  const orbit = (size: number, fraction: number, color: string, delay: number) => (
    <div className="absolute inset-0 grid place-items-center"><Ring fraction={fraction} size={size} stroke={2} color={color} delay={delay} /></div>
  );
  return (
    <div className="pane hud hud-accent relative overflow-hidden px-6 py-12 text-center">
      <Aurora />
      <div className="relative mx-auto mb-6 grid size-56 place-items-center">
        <motion.div className="absolute inset-0" animate={calm ? undefined : { rotate: 360 }} transition={{ duration: 40, repeat: Infinity, ease: "linear" }}>
          {orbit(224, 0.62, "var(--color-accent)", 0.1)}
          {orbit(176, 0.4, "var(--color-ember)", 0.3)}
        </motion.div>
        {orbit(132, 0.85, "var(--color-line-strong)", 0.5)}
        <div className="absolute size-24 rounded-full bg-accent/25 blur-2xl" aria-hidden />
        <LogoMark className="relative size-20 drop-shadow-[0_0_24px_rgb(255_61_79/0.5)]" />
      </div>
      <h2 className="font-display text-2xl font-semibold uppercase tracking-wide">Your library is empty</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted">Redeem a license key or pick a game to unlock it. Anything you own shows up here with a live countdown.</p>
      <div className="relative mt-6 flex flex-wrap justify-center gap-2">
        <Springy><Button variant="primary" size="lg" icon="card" onClick={onBrowse}>Browse games</Button></Springy>
        <Springy><Button size="lg" icon="key" onClick={onRedeem}>Redeem a key</Button></Springy>
      </div>
    </div>
  );
}

export function Library({ list, games, onExtend, onRedeem, onBrowse }: {
  list: Entitlement[]; games: Map<string, Game>; onExtend: (gameId: string) => void; onRedeem: () => void; onBrowse: () => void;
}) {
  // Cards present on first paint don't flash; anything that arrives later does.
  const initial = useRef(new Set(list.map((e) => e.game_id)));
  const ordered = [...list].sort((a, b) => Number(b.active) - Number(a.active));

  return (
    <section aria-label="Your library">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="font-display text-lg font-semibold uppercase tracking-wide">Your library</h2>
        <span className="kicker">{list.length} {list.length === 1 ? "title" : "titles"}</span>
      </div>
      {ordered.length === 0 ? <EmptyLibrary onRedeem={onRedeem} onBrowse={onBrowse} /> : (
        <motion.ul variants={stagger(0.07)} initial="hidden" animate="show" className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(min(100%,19rem),1fr))]">
          {ordered.map((e) => (
            <motion.li key={e.game_id} layout="position" variants={rise} transition={{ layout: { type: "spring", stiffness: 340, damping: 32 } }} className="min-w-0">
              <LicenseCard e={e} game={games.get(e.game_id)} fresh={!initial.current.has(e.game_id)} onExtend={() => onExtend(e.game_id)} />
            </motion.li>
          ))}
        </motion.ul>
      )}
    </section>
  );
}
